import { useState } from 'react'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Wallet,
  Building2,
  ArrowDownToLine,
  CheckCircle,
  Clock,
  XCircle,
  AlertTriangle,
  DollarSign,
} from 'lucide-react'
import { cn } from '@/lib/utils'

interface Withdrawal {
  id: string
  amount: number
  fee: number
  netAmount: number
  bankName: string
  accountNumber: string
  status: 'pending' | 'processing' | 'completed' | 'rejected'
  requestedAt: string
  processedAt?: string
}

const mockWithdrawals: Withdrawal[] = [
  { id: 'W001', amount: 10000, fee: 0, netAmount: 10000, bankName: 'กสิกรไทย', accountNumber: '***-*-*1234-5', status: 'completed', requestedAt: '2025-11-20 14:30', processedAt: '2025-11-20 15:45' },
  { id: 'W002', amount: 15000, fee: 0, netAmount: 15000, bankName: 'กสิกรไทย', accountNumber: '***-*-*1234-5', status: 'completed', requestedAt: '2025-11-10 10:15', processedAt: '2025-11-10 11:30' },
  { id: 'W003', amount: 8500, fee: 0, netAmount: 8500, bankName: 'กสิกรไทย', accountNumber: '***-*-*1234-5', status: 'completed', requestedAt: '2025-10-25 16:45', processedAt: '2025-10-25 18:00' },
  { id: 'W004', amount: 5000, fee: 0, netAmount: 5000, bankName: 'กสิกรไทย', accountNumber: '***-*-*1234-5', status: 'rejected', requestedAt: '2025-10-15 09:00' },
]

export function AffiliateWithdraw() {
  const [withdrawals] = useState(mockWithdrawals)
  const [showWithdrawDialog, setShowWithdrawDialog] = useState(false)
  const [amount, setAmount] = useState('')

  const availableBalance = 14350
  const minWithdraw = 500
  const maxWithdraw = 50000

  const getStatusBadge = (status: Withdrawal['status']) => {
    const config = {
      pending: { label: 'รอดำเนินการ', icon: <Clock className="h-3 w-3" />, className: 'bg-amber-500/10 text-amber-500' },
      processing: { label: 'กำลังดำเนินการ', icon: <Clock className="h-3 w-3" />, className: 'bg-blue-500/10 text-blue-500' },
      completed: { label: 'สำเร็จ', icon: <CheckCircle className="h-3 w-3" />, className: 'bg-win/10 text-win' },
      rejected: { label: 'ปฏิเสธ', icon: <XCircle className="h-3 w-3" />, className: 'bg-destructive/10 text-destructive' },
    }
    const { label, icon, className } = config[status]
    return <Badge variant="secondary" className={cn("border-0 gap-1", className)}>{icon} {label}</Badge>
  }

  const totalWithdrawn = withdrawals
    .filter(w => w.status === 'completed')
    .reduce((acc, w) => acc + w.netAmount, 0)

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">ถอนเงิน</h1>
          <p className="text-muted-foreground">ถอนเงินคอมมิชชั่นของคุณ</p>
        </div>
        <Button className="gap-2" onClick={() => setShowWithdrawDialog(true)}>
          <ArrowDownToLine className="h-4 w-4" />
          ถอนเงิน
        </Button>
      </div>

      {/* Balance Cards */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card className="bg-gradient-to-br from-emerald-500/10 to-teal-500/10 border-emerald-500/20">
          <CardContent className="p-6">
            <div className="flex items-center gap-3">
              <div className="p-3 rounded-lg bg-emerald-500/20">
                <Wallet className="h-6 w-6 text-emerald-500" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">ยอดถอนได้</p>
                <p className="text-3xl font-bold text-emerald-500">฿{availableBalance.toLocaleString()}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-3">
              <div className="p-3 rounded-lg bg-blue-500/10">
                <DollarSign className="h-6 w-6 text-blue-500" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">ถอนแล้วทั้งหมด</p>
                <p className="text-3xl font-bold">฿{totalWithdrawn.toLocaleString()}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-3">
              <div className="p-3 rounded-lg bg-purple-500/10">
                <Building2 className="h-6 w-6 text-purple-500" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">บัญชีธนาคาร</p>
                <p className="text-lg font-bold">กสิกรไทย</p>
                <p className="text-xs text-muted-foreground">***-*-*1234-5</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Info */}
      <Card className="bg-muted/30">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-amber-500 mt-0.5" />
            <div className="text-sm">
              <p className="font-medium mb-1">เงื่อนไขการถอน</p>
              <ul className="text-muted-foreground space-y-1">
                <li>• ถอนขั้นต่ำ ฿{minWithdraw.toLocaleString()} - สูงสุด ฿{maxWithdraw.toLocaleString()} ต่อครั้ง</li>
                <li>• ไม่มีค่าธรรมเนียม</li>
                <li>• ดำเนินการภายใน 1-24 ชั่วโมง (วันทำการ)</li>
                <li>• โอนเข้าบัญชีที่ลงทะเบียนไว้เท่านั้น</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Withdrawal History */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">ประวัติการถอน</CardTitle>
          <CardDescription>รายการถอนเงินของคุณ</CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>รหัส</TableHead>
                <TableHead className="text-right">จำนวน</TableHead>
                <TableHead>บัญชี</TableHead>
                <TableHead>สถานะ</TableHead>
                <TableHead>วันที่ขอถอน</TableHead>
                <TableHead>วันที่ดำเนินการ</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {withdrawals.map((w) => (
                <TableRow key={w.id}>
                  <TableCell className="font-mono text-sm">{w.id}</TableCell>
                  <TableCell className="text-right font-semibold">฿{w.netAmount.toLocaleString()}</TableCell>
                  <TableCell>
                    <div>
                      <p className="text-sm">{w.bankName}</p>
                      <p className="text-xs text-muted-foreground">{w.accountNumber}</p>
                    </div>
                  </TableCell>
                  <TableCell>{getStatusBadge(w.status)}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">{w.requestedAt}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">{w.processedAt || '-'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Withdraw Dialog */}
      <Dialog open={showWithdrawDialog} onOpenChange={setShowWithdrawDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>ถอนเงิน</DialogTitle>
            <DialogDescription>ระบุจำนวนเงินที่ต้องการถอน</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="p-4 rounded-lg bg-muted/30 text-center">
              <p className="text-sm text-muted-foreground">ยอดถอนได้</p>
              <p className="text-3xl font-bold text-emerald-500">฿{availableBalance.toLocaleString()}</p>
            </div>

            <div className="space-y-2">
              <Label>จำนวนเงิน (บาท)</Label>
              <Input 
                type="number" 
                placeholder="ระบุจำนวนเงิน" 
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
              />
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => setAmount('500')}>฿500</Button>
                <Button variant="outline" size="sm" onClick={() => setAmount('1000')}>฿1,000</Button>
                <Button variant="outline" size="sm" onClick={() => setAmount('5000')}>฿5,000</Button>
                <Button variant="outline" size="sm" onClick={() => setAmount(availableBalance.toString())}>ทั้งหมด</Button>
              </div>
            </div>

            <div className="p-4 rounded-lg bg-muted/30 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">โอนเข้าบัญชี</span>
                <span className="font-medium">กสิกรไทย ***-*-*1234-5</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">ค่าธรรมเนียม</span>
                <span className="font-medium text-win">ฟรี</span>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowWithdrawDialog(false)}>
              ยกเลิก
            </Button>
            <Button className="gap-2" disabled={!amount || Number(amount) < minWithdraw || Number(amount) > availableBalance}>
              <ArrowDownToLine className="h-4 w-4" />
              ยืนยันถอนเงิน
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}















